import { Grid, Stack, TextField } from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import Visible from "components/common/Visible";
import StateOfFiling from "components/inputs/program/StateOfFiling";
import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as programActions from "store/actions/input/program/programActions";
import {
  selectStateOfFiling,
  selectSurplusLinesTaxReporting,
} from "store/selectors/input/program/programSelectors";
import * as peopleSelectors from "store/selectors/people/peopleSelectors";

const FIELDS = [
  { id: "taxBrokerCompany", label: "SL Tax Broker Company" },
  { id: "taxBrokerName", label: "SL Tax Broker Name" },
  { id: "taxBrokerAddress", label: "SL Tax Broker Address", multiline: true },
  { id: "brokerCity", label: "SL Broker City" },
  { id: "brokerState", label: "SL Broker State" },
  { id: "brokerZip", label: "SL Broker Zip" },
  { id: "licenseNumber", label: "SL License Number" },
  { id: "brokerNpn", label: "SL Broker NPN" },
  { id: "njTransactionNumber", label: "NJ Transaction Number" },
];

const seedFromBroker = (broker) => {
  if (!broker) return null;
  const seed = {
    taxBrokerCompany: broker.company ?? "",
    taxBrokerName: broker.name ?? "",
    taxBrokerAddress: broker.address ?? "",
    licenseNumber: broker.licenseNumber ?? "",
  };
  const hasAnything = Object.values(seed).some((v) => v !== "");
  return hasAnything ? seed : null;
};

const SurplusLinesTax = () => {
  const dispatch = useDispatch();
  const reporting = useSelector(selectSurplusLinesTaxReporting);
  const stateOfFiling = useSelector(selectStateOfFiling);
  const broker = useSelector(peopleSelectors.selectBroker);

  const seedPayload = useMemo(() => seedFromBroker(broker), [broker]);

  useEffect(() => {
    if (seedPayload && Object.keys(reporting).length === 0) {
      dispatch(programActions.seedSurplusLinesTaxReporting(seedPayload));
    }
  }, [dispatch, reporting, seedPayload]);

  return (
    <Disabled ifReadOnly>
      <Component
        title={"Surplus Lines Tax Reporting"}
        subtitle={
          "These fields carry over to the rater export for bordereaux reporting. They default to the broker of record on the submission and can be edited if the SL tax broker is different."
        }
      >
        <Grid container spacing={2}>
          {FIELDS.map((field) => (
            <Grid item xs={12} sm={6} key={field.id}>
              <TextField
                label={field.label}
                value={reporting[field.id] ?? ""}
                onChange={(event) =>
                  dispatch(
                    programActions.updateSurplusLinesTaxReporting(
                      field.id,
                      event.target.value
                    )
                  )
                }
                variant={"filled"}
                size={"small"}
                fullWidth
                multiline={field.multiline ?? false}
                minRows={field.multiline ? 3 : undefined}
              />
            </Grid>
          ))}
          {/* The program-level State of Filing, the same value as on the Program
              tab. It has its own tag so a company can show it here, on the Program
              tab, or both. Last so toggling the tag doesn't shift the fields above it. */}
          <Visible
            byTag={"input.surplus-lines-tax.stateOfFiling"}
            defaultVisible={false}
          >
            <Grid item xs={12} sm={6}>
              <StateOfFiling
                value={stateOfFiling}
                onChange={(value) =>
                  dispatch(programActions.updateStateOfFiling(value))
                }
              />
            </Grid>
          </Visible>
        </Grid>
        <Stack sx={{ mt: 2 }} />
      </Component>
    </Disabled>
  );
};

export default SurplusLinesTax;
